import "./style.css";
import { CsvTransformError, TransformSummary, transformCsvFile } from "./csvTransform";

type ViewState = "idle" | "ready" | "processing" | "done" | "error";

const app = document.querySelector<HTMLDivElement>("#app");

if (!app) {
  throw new Error("App root was not found.");
}

function requireElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) {
    throw new Error(`Required UI element was not found: ${selector}`);
  }
  return element;
}

app.innerHTML = `
  <main class="app-shell">
    <section class="toolbar" aria-label="CSV列変換">
      <div>
        <p class="eyebrow">Cloudflare Pages ready</p>
        <h1>B2 CSV列変換</h1>
      </div>
      <div class="spec-grid" aria-label="変換設定">
        <span>28列目: ホビー</span>
        <span>30列目: 空白</span>
        <span>出力: CP932 / CRLF</span>
      </div>
    </section>

    <section class="workbench">
      <div class="dropzone" id="dropzone">
        <input class="file-input" id="csvInput" type="file" accept=".csv,text/csv" />
        <label class="file-label" for="csvInput">
          <span class="file-label-main" id="fileLabelMain">CSVを選択</span>
          <span class="file-label-sub" id="fileLabelSub">またはここへドロップ</span>
        </label>
      </div>

      <div class="actions">
        <button class="primary-button" id="convertButton" type="button" disabled>変換</button>
        <a class="download-button is-disabled" id="downloadLink" aria-disabled="true">ダウンロード</a>
      </div>

      <output class="status-panel" id="statusPanel" aria-live="polite">
        <span class="status-label">待機中</span>
        <span class="status-text">CSVファイルを選択してください。</span>
      </output>
    </section>

    <section class="summary-panel" id="summaryPanel" aria-label="変換結果" hidden>
      <div class="summary-card">
        <span class="summary-value" id="rowCount">0</span>
        <span class="summary-label">行</span>
      </div>
      <div class="summary-card">
        <span class="summary-value" id="columnRange">0</span>
        <span class="summary-label">列</span>
      </div>
      <div class="summary-card">
        <span class="summary-value" id="downloadName">-</span>
        <span class="summary-label">出力ファイル</span>
      </div>
    </section>
  </main>
`;

const dropzone = requireElement<HTMLDivElement>("#dropzone");
const csvInput = requireElement<HTMLInputElement>("#csvInput");
const convertButton = requireElement<HTMLButtonElement>("#convertButton");
const downloadLink = requireElement<HTMLAnchorElement>("#downloadLink");
const fileLabelMain = requireElement<HTMLSpanElement>("#fileLabelMain");
const fileLabelSub = requireElement<HTMLSpanElement>("#fileLabelSub");
const statusPanel = requireElement<HTMLOutputElement>("#statusPanel");
const summaryPanel = requireElement<HTMLElement>("#summaryPanel");
const rowCount = requireElement<HTMLSpanElement>("#rowCount");
const columnRange = requireElement<HTMLSpanElement>("#columnRange");
const downloadName = requireElement<HTMLSpanElement>("#downloadName");

let selectedFile: File | null = null;
let activeDownloadUrl: string | null = null;

function setStatus(state: ViewState, label: string, text: string, details: string[] = []): void {
  statusPanel.className = `status-panel status-${state}`;
  statusPanel.innerHTML = `
    <span class="status-label">${escapeHtml(label)}</span>
    <span class="status-text">${escapeHtml(text)}</span>
    ${details.length > 0 ? `<ul>${details.map((detail) => `<li>${escapeHtml(detail)}</li>`).join("")}</ul>` : ""}
  `;
}

function resetDownload(): void {
  if (activeDownloadUrl) {
    URL.revokeObjectURL(activeDownloadUrl);
    activeDownloadUrl = null;
  }
  downloadLink.removeAttribute("href");
  downloadLink.removeAttribute("download");
  downloadLink.classList.add("is-disabled");
  downloadLink.setAttribute("aria-disabled", "true");
  summaryPanel.hidden = true;
}

function setSelectedFile(file: File | null): void {
  selectedFile = file;
  resetDownload();
  convertButton.disabled = !file;

  if (!file) {
    fileLabelMain.textContent = "CSVを選択";
    fileLabelSub.textContent = "またはここへドロップ";
    setStatus("idle", "待機中", "CSVファイルを選択してください。");
    return;
  }

  fileLabelMain.textContent = file.name;
  fileLabelSub.textContent = `${formatBytes(file.size)} / 変換待ち`;
  setStatus("ready", "準備完了", "変換できます。");
}

function setSummary(summary: TransformSummary): void {
  rowCount.textContent = summary.rowCount.toLocaleString("ja-JP");
  columnRange.textContent =
    summary.minColumns === summary.maxColumns
      ? summary.maxColumns.toLocaleString("ja-JP")
      : `${summary.minColumns.toLocaleString("ja-JP")} - ${summary.maxColumns.toLocaleString("ja-JP")}`;
  downloadName.textContent = summary.downloadName;
  summaryPanel.hidden = false;
}

async function convertSelectedFile(): Promise<void> {
  if (!selectedFile) {
    return;
  }

  resetDownload();
  convertButton.disabled = true;
  setStatus("processing", "処理中", "CSVを変換しています。");

  try {
    const result = await transformCsvFile(selectedFile);
    activeDownloadUrl = URL.createObjectURL(result.blob);
    downloadLink.href = activeDownloadUrl;
    downloadLink.download = result.summary.downloadName;
    downloadLink.classList.remove("is-disabled");
    downloadLink.setAttribute("aria-disabled", "false");
    fileLabelSub.textContent = `${formatBytes(selectedFile.size)} / 変換済み`;
    setSummary(result.summary);
    setStatus(
      "done",
      "完了",
      `${result.summary.rowCount.toLocaleString("ja-JP")}行を変換しました。`
    );
  } catch (error) {
    const details = error instanceof CsvTransformError ? error.details : [];
    const message = error instanceof Error ? error.message : "変換に失敗しました。";
    setStatus("error", "エラー", message, details);
  } finally {
    convertButton.disabled = !selectedFile;
  }
}

function handleFiles(files: FileList | null): void {
  const file = files?.[0] ?? null;
  setSelectedFile(file);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      default:
        return "&#039;";
    }
  });
}

csvInput.addEventListener("change", () => handleFiles(csvInput.files));
convertButton.addEventListener("click", () => void convertSelectedFile());

dropzone.addEventListener("dragover", (event) => {
  event.preventDefault();
  dropzone.classList.add("is-dragover");
});

dropzone.addEventListener("dragleave", () => {
  dropzone.classList.remove("is-dragover");
});

dropzone.addEventListener("drop", (event) => {
  event.preventDefault();
  dropzone.classList.remove("is-dragover");
  handleFiles(event.dataTransfer?.files ?? null);
});
